import React from 'react';
import { 
  Typography, 
  Container, 
  Box, 
  Button, 
  Paper, 
  Stepper, 
  Step, 
  StepLabel, 
  StepContent, 
  Grid, 
  Card, 
  CardContent, 
  Divider, 
  useTheme, 
  alpha,
  useMediaQuery
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PostAddIcon from '@mui/icons-material/PostAdd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleScheduleDemo = (): void => {
    navigate('/contact', { state: { subject: 'Demo Request' } });
  };

  const steps = [
    {
      label: 'Provide job description',
      description: 'Give RiC your job description and answer a few questions.',
      icon: <DescriptionIcon />,
      color: theme.palette.primary.main
    },
    {
      label: 'AI Analysis',
      description: 'Our AI meticulously analyzes skills, experience, and qualifications.',
      icon: <SmartToyIcon />,
      color: theme.palette.primary.dark
    },
    {
      label: 'Candidate Search',
      description: 'Receive a ranked list of candidates based on your specific job requirements.',
      icon: <SearchIcon />,
      color: theme.palette.secondary.main
    },
    {
      label: 'Candidate Filtering',
      description: 'Utilize detailed analytics to make informed hiring decisions.',
      icon: <PsychologyIcon />,
      color: theme.palette.secondary.dark
    },
    {
      label: 'Jobs Posted',
      description: 'Jobs get put on up to 75+ Major Job Boards.',
      icon: <PostAddIcon />,
      color: theme.palette.success.main
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header Section */}

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="div"
            sx={{ 
              color: 'primary.main',
              fontWeight: 600,
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
              letterSpacing: 1.5,
              mb: 2,
              p:4,
              textTransform: 'uppercase'
            }}
          >
            How RECRUITMENT INTELLIGENCE<span style={{ fontSize: '0.6em', verticalAlign: 'super' }}>®</span> Works
          </Typography>
          {/* <Divider sx={{ width: '80px', mx: 'auto', mb: 3, borderWidth: 2, borderColor: 'primary.main' }} /> */}
        </Box>

        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            textAlign: 'center', 
            maxWidth: '800px', 
            mx: 'auto',
            fontSize: '1.1rem',
            lineHeight: 1.6,
            mb: 4
          }}
        >
          RiC simplifies the recruiting process with cutting-edge AI to make hiring smarter, faster, and far more cost effective.
        </Typography>


      {/* Process Section - Desktop View */}
      {!isMobile && (
        <Paper elevation={3} sx={{ p: 4, mb: 5, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <RocketLaunchIcon sx={{ color: 'secondary.main', mr: 1, fontSize: 30 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              The Process
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 4 }} />
          
          <Grid container spacing={2} sx={{ position: 'relative' }}>
            {/* Process Steps */}
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      mb: 3,
                      position: 'relative',
                      borderLeft: `4px solid ${step.color}`,
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', p: 3 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          backgroundColor: alpha(step.color, 0.1),
                          borderRadius: '50%',
                          p: 1.5,
                          mr: 3,
                          color: step.color
                        }}
                      >
                        {step.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {index + 1}. {step.label}
                        </Typography>
                        <Typography variant="body1">
                          {step.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Arrow connector (except for last item) */}
 {/*                {index < steps.length - 1 && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <ArrowForwardIcon 
                      sx={{ 
                        transform: 'rotate(90deg)', 
                        color: theme.palette.text.secondary,
                        fontSize: 28
                      }} 
                    />
                  </Grid>
                )} */}
              </React.Fragment>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Process Section - Mobile View */}
      {isMobile && (
        <Paper elevation={3} sx={{ p: 3, mb: 5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <RocketLaunchIcon sx={{ color: 'secondary.main', mr: 1, fontSize: 24 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              The Process
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 3 }} />
          
          <Stepper orientation="vertical" sx={{ mb: 3 }}>
            {steps.map((step, index) => (
              <Step key={index} active={true}>
                <StepLabel 
                  StepIconProps={{ 
                    icon: step.icon,
                    style: { color: step.color } 
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      {/* Conclusion Section */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mb: 5, 
          borderRadius: 2,
          background: `linear-gradient(to right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)}), url('/assets/pattern-bg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            fontSize: '1.1rem', 
            fontWeight: 500,
            textAlign: 'center',
            maxWidth: '900px',
            mx: 'auto',
            mb: 4
          }}
        >
          Experience a seamless and intelligent approach to talent acquisition with Recruitment Intelligence.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large"
            onClick={handleScheduleDemo}
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              py: 1.5,
              px: 4,
              borderRadius: '30px',
              boxShadow: `0 8px 16px ${alpha(theme.palette.secondary.main, 0.3)}`,
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 20px ${alpha(theme.palette.secondary.main, 0.4)}`
              }
            }}
          >
            Schedule a Demo
          </Button>
        </Box>
      </Paper>

      {/* Comparison Image Section */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            textAlign: 'center', 
            mb: 3,
            fontWeight: 600
          }}
        >
          See How We Compare to LinkedIn Recruiter and common Job Boards
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: `0 5px 15px ${alpha(theme.palette.common.black, 0.1)}`
          }}
        >
          <Box
            component="img"
            src="/assets/LinkedInCompare2.png"
            alt="LinkedIn Comparison"
            sx={{
              width: '100%',
              maxWidth: '900px',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default HowItWorksPage;
